import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { publicApi } from '@/lib/api';
import { PUBLIC_STALE_MS } from '@/lib/queryClient';
import type { PostListParams } from '@prompthijack/shared';

export const POSTS_KEYS = {
  all:      ['posts']                                        as const,
  list:     (params?: PostListParams) => ['posts', 'list', params] as const,
  infinite: (params?: PostListParams) => ['posts', 'infinite', params] as const,
  detail:   (slug: string)            => ['posts', 'detail', slug]   as const,
  featured: ()                        => ['posts', 'featured']        as const,
};

export function usePosts(params?: PostListParams) {
  return useQuery({
    queryKey:  POSTS_KEYS.list(params),
    queryFn:   () => publicApi.getPosts(params),
    staleTime: PUBLIC_STALE_MS,
  });
}

export function useInfinitePosts(params?: Omit<PostListParams, 'page'>) {
  return useInfiniteQuery({
    queryKey: POSTS_KEYS.infinite(params),
    queryFn:  ({ pageParam }) =>
      publicApi.getPosts({ ...params, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined,
    staleTime: PUBLIC_STALE_MS,
  });
}

export function usePost(slug: string) {
  return useQuery({
    queryKey: POSTS_KEYS.detail(slug),
    queryFn:  () => publicApi.getPost(slug),
    enabled:  !!slug,
  });
}

export function useFeaturedPosts() {
  return useQuery({
    queryKey:  POSTS_KEYS.featured(),
    queryFn:   publicApi.getFeaturedPosts,
    staleTime: PUBLIC_STALE_MS * 2.5, // 5 min
  });
}

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/api';
import type { CreatePostPayload, UpdatePostPayload } from '@prompthijack/shared';

export function useCreatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePostPayload) => adminApi.createPost(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: POSTS_KEYS.all }),
  });
}

export function useUpdatePost(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdatePostPayload) => adminApi.updatePost(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: POSTS_KEYS.all }),
  });
}

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApi.deletePost(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: POSTS_KEYS.all }),
  });
}
